import { Injectable, inject } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import {
    Observable,
    forkJoin,
    from,
    Subject,
    EMPTY,
    lastValueFrom
} from 'rxjs';
import {
    map,
    mergeMap,
    finalize,
    catchError,
    shareReplay,
    take,
    tap
} from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { BookingsRepository } from './repositories/bookings.repository';
import { CustomersRepository } from './repositories/customers.repository';
import { BookingsResource } from './resources/bookings.resource';
import { CustomersResource } from './resources/customers.resource';
import { RepositoryService } from './repository.service';
import { TableStateResource } from './resources/table-state.resource';
import { TableStateModel } from '../models/table-state.model';
import { SynchronizableTables } from '../core/utils/synchronizable-tables';
import { AllocationsResource } from './resources/allocations.resource';
import { TableStateRepository } from './repositories/table-state.repository';
import { AllocationsRepository } from './repositories/allocations.repository';
import { SearchResponseModel } from '../core/models/search-response.model';
import { DictionaryType } from '../core/models/types/dictionary.type';
import { IndexResource } from './resources/index.resource';
import { delayedRetry } from '../core/utils/delayed-retry';
import { SyncStorage } from './storages/sync.storage';
import { AlertService } from './alert.service';
import { AuthService } from './auth.service';
import { StaticReferences } from '../core/utils/static-references';
import { UsersResource } from './resources/users.resource';
import { ProvidersRepository } from './repositories/providers.repository';
import { TodosResource } from './resources/todos.resource';
import { TodosRepository } from './repositories/todos-repository';
import { TemplatesRepository } from './repositories/templates.repository';
import { TemplateResource } from './resources/template.resource';
import { Router } from '@angular/router';

@Injectable({
    providedIn: 'root'
})
export class SyncService {
    private swUpdate = inject(SwUpdate);
    private bookingsResource = inject(BookingsResource);
    private customersResource = inject(CustomersResource);
    private todosResource = inject(TodosResource);
    private allocationsResource = inject(AllocationsResource);
    private tableStateResource = inject(TableStateResource);
    private indexResource = inject(IndexResource);
    private usersResource = inject(UsersResource);
    private bookingsRepository = inject(BookingsRepository);
    private customersRepository = inject(CustomersRepository);
    private todosRepository = inject(TodosRepository);
    private allocationsRepository = inject(AllocationsRepository);
    private tableStateRepository = inject(TableStateRepository);
    private providersRepository = inject(ProvidersRepository);
    private authService = inject(AuthService);
    private alertService = inject(AlertService);
    private repositoryService = inject(RepositoryService);
    private syncStorage = inject(SyncStorage);
    private templatesRepository = inject(TemplatesRepository);
    private templateResource = inject(TemplateResource);
    private router = inject(Router);

    reloadIfUpdatesAvailable = false;
    isAppLocked = false;
    get isSyncing(): boolean {
        return this._isSyncing;
    }
    readonly isOnline = new Subject<boolean>();
    readonly hasSyncFinished = new Subject<boolean>();
    readonly progressChanged = new Subject<string>();

    private _isSyncing = false;
    private _isFullSyncForced = false;
    private currentServerStates: TableStateModel[] = [];
    private tableUpdateRequests: Observable<any>[] = [];
    private tableNamesUpdate: string[] = [];
    private tableServerUpdateRequests: Observable<any>[] = [];

    constructor() {
        this.swUpdate.versionUpdates.subscribe(event => {
            this.swUpdate.activateUpdate().then(() => {
                if (this.reloadIfUpdatesAvailable) {
                    document.location.reload();
                }
            });
        });
    }

    start(): void {
        this.repositoryService.setup().then();
    }

    checkOnlineStatus(): void {
        this.indexResource
            .ping()
            .pipe(
                delayedRetry(1000),
                catchError(err => {
                    this.isOnline.next(false);
                    return EMPTY;
                }),
                shareReplay()
            )
            .subscribe(r => this.isOnline.next(!!r));
    }

    validateSyncRequirement(): void {
        if (this._isSyncing) {
            return;
        }

        const lastSync = this.syncStorage.getLastSync();

        if (!lastSync) {
            this.authService.logout();
            return;
        }

        const now = new Date();
        const dateDiff = Math.abs(now.getTime() - lastSync.getTime());
        const daysWithoutUpdate = Math.ceil(dateDiff / (1000 * 3600 * 24));
        const warningThreshold =
            environment.notSyncedMaxDays -
            environment.notSyncedPreMaxDaysWarning;

        if (
            daysWithoutUpdate >= warningThreshold &&
            daysWithoutUpdate < environment.notSyncedMaxDays
        ) {
            // Warning phase: we tell the user if no update is done soon, he'll lose access.
            const remainingSyncDays =
                environment.notSyncedMaxDays - daysWithoutUpdate;
            this.alertService.showAlert(
                `You have ${remainingSyncDays} days to sync, otherwise you'll lose access to the app.`
            );
        } else if (daysWithoutUpdate >= environment.notSyncedMaxDays) {
            // Block phase: we prevent the user from accessing the app until syncing.
            this.isAppLocked = true;

            this.alertService.showError(
                `You haven't synced in ${daysWithoutUpdate} days.
                You won't be able to use the app until you go online and sync.`
            );
            this.authService.logout();
        }
    }

    sync(): void {
        this.isOnline.pipe(take(1)).subscribe(r => {
            if (r) {
                this.performSync();
            }
        });
    }

    private async performSync(): Promise<void> {
        if (this._isSyncing) {
            return;
        }

        this._isSyncing = true;

        this.progressChanged.next('Loading website files');

        // Enable only when running on server.
        if (environment.production) {
            this.swUpdate.checkForUpdate();
        }

        if (this._isFullSyncForced) {
            this.progressChanged.next('Refreshing local database');

            await Promise.all([
                this.allocationsRepository.deleteAll(),
                this.bookingsRepository.deleteAll(),
                this.customersRepository.deleteAll(),
                this.providersRepository.deleteAll(),
                this.todosRepository.deleteAll(),
                StaticReferences.unsyncedChangesRepository.deleteAll(),
                this.templatesRepository.deleteAll()
            ]);

            this._isFullSyncForced = false;
        }

        this.progressChanged.next('Validating sync requirements');

        try {
            await this.validateSync();

            if (this._isFullSyncForced) {
                return;
            }
        } catch (err) {
            // If there's any error with validateSync then something is probably wrong with the local DB.
            this.progressChanged.next('Updating local database');

            await Promise.all([
                this.allocationsRepository.deleteAll(),
                this.bookingsRepository.deleteAll(),
                this.customersRepository.deleteAll(),
                this.providersRepository.deleteAll(),
                this.todosRepository.deleteAll(),
                StaticReferences.unsyncedChangesRepository.deleteAll(),
                this.templatesRepository.deleteAll()
            ]);

            await this.validateSync();
        }

        this.progressChanged.next('Checking server');

        if (
            this.tableUpdateRequests.length > 0 &&
            this.tableServerUpdateRequests.length > 0
        ) {
            // Updates server and client.
            await lastValueFrom(
                forkJoin(this.tableUpdateRequests).pipe(
                    mergeMap(r => this.syncRepositories(r).pipe(map(() => {}))),
                    finalize(() =>
                        forkJoin(this.tableServerUpdateRequests)
                            .pipe(
                                mergeMap(r =>
                                    this.syncRepositories(r).pipe(map(() => {}))
                                ),
                                finalize(() => this.finishSync())
                            )
                            .subscribe()
                    )
                )
            );
        } else if (this.tableUpdateRequests.length > 0) {
            // Only updates the client.
            await lastValueFrom(
                forkJoin(this.tableUpdateRequests).pipe(
                    mergeMap(r => this.syncRepositories(r).pipe(map(() => {}))),
                    finalize(() => this.finishSync())
                )
            );
        } else if (this.tableServerUpdateRequests.length > 0) {
            // Only updates the server.
            await lastValueFrom(
                forkJoin(this.tableServerUpdateRequests).pipe(
                    finalize(() => {
                        StaticReferences.unsyncedChangesRepository
                            .deleteAll()
                            .then();
                        this.finishSync();
                    })
                )
            );
        } else {
            // No updates required.
            this.finishSync();
        }

        return;
    }

    private finishSync(): void {
        this.syncStorage.saveLastSync(new Date());

        if (!this.syncStorage.lastFullSyncExists()) {
            this.syncStorage.saveLastFullSync(new Date());
        }

        this.hasSyncFinished.next(true);

        this.tableUpdateRequests = [];
        this.tableServerUpdateRequests = [];
        this.isAppLocked = false;
        this._isSyncing = false;
    }

    private async validateSync(): Promise<void> {
        if (this.syncStorage.lastFullSyncExists()) {
            const shouldForceFullSync = await lastValueFrom(
                this.tableStateResource.validateFullSync(
                    this.syncStorage.getLastFullSync()
                )
            );

            if (shouldForceFullSync) {
                await this.forceFullSync();
                return;
            }
        }

        this.currentServerStates = await lastValueFrom(
            this.tableStateResource.getCurrent()
        );
        const localStates = await this.tableStateRepository.getAll();
        const updatedLocalStates: DictionaryType<Date> = {};

        for (const serverState of this.currentServerStates) {
            const hasLocalRecords = await this.hasLocalRecords(serverState.id);
            const localState = await this.tableStateRepository.getById(
                serverState.id
            );

            if (
                !hasLocalRecords ||
                !localState ||
                (localState.lastLocalUpdate &&
                    new Date(serverState.modified) >
                        new Date(localState.lastLocalUpdate))
            ) {
                this.setupUpdateFromServer(
                    serverState.id,
                    localState ? localState.lastLocalUpdate : undefined,
                    !hasLocalRecords
                );
                updatedLocalStates[serverState.id] = serverState.modified;
            } else if (
                new Date(serverState.modified) <
                new Date(localState.lastLocalUpdate)
            ) {
                this.updateFromLocal(localState.id);
            }
        }

        // We need to update the server state with the local information so it's not lost on sync.
        this.currentServerStates.forEach(css => {
            let localUpdate = updatedLocalStates[css.id];

            if (!localUpdate) {
                localUpdate = localStates.find(
                    ls => ls.id === css.id
                ).lastLocalUpdate;
            }

            css.lastLocalUpdate = localUpdate;
        });

        await this.tableStateRepository.editRange(this.currentServerStates);

        return;
    }

    private updateFromLocal(tableName: string): void {
        let observable: Observable<any>;
        switch (tableName) {
            case SynchronizableTables.Allocations:
                observable = this.allocationsResource
                    .syncToServer(tableName)
                    .pipe(
                        tap(() =>
                            this.progressChanged.next('Uploading allocations')
                        )
                    );
                break;
            case SynchronizableTables.Bookings:
                observable = this.bookingsResource
                    .syncToServer(tableName)
                    .pipe(
                        tap(() =>
                            this.progressChanged.next('Uploading bookings')
                        )
                    );
                break;
            case SynchronizableTables.Customers:
                observable = this.customersResource
                    .syncToServer(tableName)
                    .pipe(
                        tap(() =>
                            this.progressChanged.next('Uploading customers')
                        )
                    );
                break;
            case SynchronizableTables.Todos:
                observable = this.todosResource
                    .syncToServer(tableName)
                    .pipe(
                        tap(() => this.progressChanged.next('Uploading todos'))
                    );
                break;
            default:
                break;
        }

        if (observable) {
            this.tableServerUpdateRequests.push(observable);
        }
    }

    private setupUpdateFromServer(
        tableName: string,
        localUpdate?: Date,
        isFirstLoad = false
    ): void {
        let observable: Observable<any>;
        switch (tableName) {
            case SynchronizableTables.Allocations:
                observable = this.allocationsResource
                    .syncFromServer(localUpdate, isFirstLoad)
                    .pipe(
                        tap(() =>
                            this.progressChanged.next('Downloading allocations')
                        )
                    );
                break;
            case SynchronizableTables.Bookings:
                observable = this.bookingsResource
                    .syncFromServer(localUpdate, isFirstLoad)
                    .pipe(
                        tap(() =>
                            this.progressChanged.next('Downloading bookings')
                        )
                    );
                break;
            case SynchronizableTables.Customers:
                observable = this.customersResource
                    .syncFromServer(localUpdate, isFirstLoad)
                    .pipe(
                        tap(() =>
                            this.progressChanged.next('Downloading customers')
                        )
                    );
                break;
            case SynchronizableTables.Providers:
                observable = this.usersResource
                    .syncFromServer(localUpdate, isFirstLoad)
                    .pipe(
                        tap(() =>
                            this.progressChanged.next('Downloading providers')
                        )
                    );
                break;
            case SynchronizableTables.Todos:
                observable = this.todosResource
                    .syncFromServer(localUpdate, isFirstLoad)
                    .pipe(
                        tap(() =>
                            this.progressChanged.next('Downloading todos')
                        )
                    );
                break;
            case SynchronizableTables.Templates:
                observable = this.templateResource
                    .syncFromServer(localUpdate, isFirstLoad)
                    .pipe(
                        tap(() =>
                            this.progressChanged.next('Downloading templates')
                        )
                    );
                break;
            default:
                break;
        }

        if (observable) {
            if (this.tableNamesUpdate.includes(tableName)) {
                return;
            }

            this.tableNamesUpdate.push(tableName);
            this.tableUpdateRequests.push(observable);
        }
    }

    private syncRepositories(
        resp: SearchResponseModel<any>[]
    ): Observable<void> {
        const syncRequests: Observable<void>[] = [];

        for (let i = 0; i < this.tableNamesUpdate.length; i++) {
            const tableName = this.tableNamesUpdate[i];

            // we can safely do resp[i] because forkJoin always returns the response in
            // the order they were sent
            switch (tableName) {
                case SynchronizableTables.Allocations:
                    syncRequests.push(
                        from(this.allocationsRepository.sync(resp[i])).pipe(
                            tap(() =>
                                this.progressChanged.next('Syncing allocations')
                            )
                        )
                    );
                    break;
                case SynchronizableTables.Bookings:
                    syncRequests.push(
                        from(this.bookingsRepository.sync(resp[i])).pipe(
                            tap(() =>
                                this.progressChanged.next('Syncing bookings')
                            )
                        )
                    );
                    break;
                case SynchronizableTables.Customers:
                    syncRequests.push(
                        from(this.customersRepository.sync(resp[i])).pipe(
                            tap(() =>
                                this.progressChanged.next('Syncing customers')
                            )
                        )
                    );
                    break;
                case SynchronizableTables.Todos:
                    syncRequests.push(
                        from(this.todosRepository.sync(resp[i])).pipe(
                            tap(() =>
                                this.progressChanged.next('Syncing todos')
                            )
                        )
                    );
                    break;
                case SynchronizableTables.Providers:
                    syncRequests.push(
                        from(this.providersRepository.sync(resp[i])).pipe(
                            tap(() =>
                                this.progressChanged.next('Syncing providers')
                            )
                        )
                    );
                    break;
                case SynchronizableTables.Templates:
                    syncRequests.push(
                        from(this.templatesRepository.sync(resp[i])).pipe(
                            tap(() => {
                                this.progressChanged.next('Syncing templates');
                            })
                        )
                    );
                    break;
                default:
                    break;
            }
        }

        return forkJoin(syncRequests).pipe(
            map(() => {}),
            finalize(() => (this.tableNamesUpdate = []))
        );
    }

    private async hasLocalRecords(tableName: string): Promise<boolean> {
        let promise: Promise<number>;

        switch (tableName) {
            case SynchronizableTables.Allocations:
                promise = this.allocationsRepository.count();
                break;
            case SynchronizableTables.Bookings:
                promise = this.bookingsRepository.count();
                break;
            case SynchronizableTables.Customers:
                promise = this.customersRepository.count();
                break;
            case SynchronizableTables.Todos:
                promise = this.todosRepository.count();
                break;
            case SynchronizableTables.Providers:
                promise = this.providersRepository.count();
                break;
            case SynchronizableTables.Templates:
                promise = this.templatesRepository.count();
                break;
            default:
                break;
        }

        return promise ? (await promise) > 0 : false;
    }

    private async forceFullSync() {
        const result = await this.alertService.showCustomConfirm(
            'You may lost your work',
            'Full sync required',
            'Ok'
        );

        if (result.isConfirmed) {
            this._isSyncing = false;
            this._isFullSyncForced = true;

            this.syncStorage.eraseLastFullSync();

            await this.router.navigate(['data-sync']);
        }
    }
}
